import React, { useEffect, useRef, useState } from 'react';

import { Button, Input, Select, notification, message, Modal, Typography, Form, FormItemProps } from 'antd';
import UserStore from 'stores/UserStore';
import SelectCountries from 'components/shared/SelectCountries';
import SelectStates from 'components/shared/SelectStates';
import ErrorRender from 'components/shared/ErrorRender';

import { IAddresses } from 'types/types';
import { PlusOutlined } from '@ant-design/icons';
import { FormErrorParser } from '@partsbadger/library';

const { Title } = Typography;

interface IProps {
  record?: IAddresses;
  updateData: () => void;
}

const AddEdit: React.FC<IProps> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);

  const [form] = Form.useForm();

  // Hidden native inputs (browser autofill targets these reliably)
  const streetRef  = useRef<HTMLInputElement>(null);
  const cityRef    = useRef<HTMLInputElement>(null);
  const stateRef   = useRef<HTMLInputElement>(null);
  const zipRef     = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLInputElement>(null);

  // --- Helpers --------------------------------------------------------------

  // Light country normalization (extend as needed)
  const toCountryObj = (raw?: string) => {
    if (!raw) return undefined;
    const v = raw.trim();
    const cc = v.toUpperCase();
    if (cc === 'US' || /United States/i.test(v)) return { key: 'US', label: 'United States' };
    if (cc === 'EC' || /Ecuador/i.test(v))       return { key: 'EC', label: 'Ecuador' };
    // If 2-letter code, assume ISO alpha-2
    if (/^[A-Z]{2}$/.test(cc)) return { key: cc, label: v };
    // Fallback: use label as-is; your SelectCountries should still accept it
    return { key: v, label: v };
  };

  const toStateObj = (raw?: string) => {
    if (!raw) return undefined;
    const v = raw.trim();
    // If your SelectStates expects a short code, you can map here.
    return { key: v, label: v };
  };

  const syncFromNativeAutofill = () => {
    const street  = streetRef.current?.value?.trim();
    const city    = cityRef.current?.value?.trim();
    const state   = stateRef.current?.value?.trim();
    const zip     = zipRef.current?.value?.trim();
    const country = countryRef.current?.value?.trim();

    const next: any = {};
    if (street)  next.street   = street;
    if (city)    next.city     = city;
    if (zip)     next.zip_code = zip;
    if (country) next.country  = toCountryObj(country);
    if (state)   next.state    = toStateObj(state);

    if (Object.keys(next).length) {
      form.setFieldsValue(next);
    }
  };

  // Poll briefly after modal opens to capture autofill fills
  useEffect(() => {
    if (!visible) return;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      syncFromNativeAutofill();
      if (ticks > 15) clearInterval(id); // ~3s at 200ms
    }, 200);
    return () => clearInterval(id);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (visible && props.record) {
        form.setFieldsValue({
        name: props.record.name,
        type: props.record.type,
        street: props.record.street,
        city: props.record.city,
        zip_code: props.record.zip_code,
        country: { key: props.record.country.code, label: props.record.country.name },
        state: { key: props.record.state, label: props.record.state },
        });
    }
   }, [visible, props.record, form]);


  const handleSubmit = async () => {
    form
      .validateFields()
      .then(async (values) => {
        const id = UserStore.user?.default_contact?.id;

        const normCountry = typeof values.country === 'string'
          ? toCountryObj(values.country)
          : values.country;

        const normState = typeof values.state === 'string'
          ? toStateObj(values.state)
          : values.state;

        const data = {
          ...values,
          street: values.street,
          country: normCountry?.key,
          state: normState?.key,
        };

        setLoading(true);

        if (props.record) {
          return await UserStore
            .editAddress(data, props.record.id)
            .then(() => {
              props.updateData();
              setLoading(false);
              setVisible(false);
              message.success(`Successfully edited!!!`);
            })
            .catch((error) => {
              setLoading(false);
              notification.error({
                message: 'Error',
                description: <ErrorRender error={error} />,
              });
            });
        }

        if (id) {
          await UserStore
            .addAddress(id, data)
            .then(() => {
              props.updateData();
              setLoading(false);
              setVisible(false);
              message.success(`Successfully created!!!`);
            })
            .catch((error) => {
              setLoading(false);
              notification.error({
                message: 'Error',
                description: <ErrorRender error={error} />,
              });
            });
        }
      })
      .catch((errorInfo) => {
        notification.error({
          key: 'form-errors',
          duration: 8,
          message: 'Please input all required fields',
          description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
          placement: 'top',
          maxCount: 1,
        });
      });
  };

  const formItemLayout: FormItemProps = {
    labelCol: { md: { span: 24 } },
    wrapperCol: { md: { span: 24 } },
    labelAlign: 'left',
  };

  return (
    <>
      {props.record ? (
        <Button onClick={() => {  setVisible(!visible); }} loading={loading} size="small">
          Edit
        </Button>
      ) : (
        <div style={{ textAlign: 'center', margin: 'auto' }}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => { form.resetFields(); setVisible(!visible);}}
            loading={loading}
            type="primary"
            size="small"
            className="mt-3"
          >
            Add Address
          </Button>
        </div>
      )}

      <Modal
        open={visible}
        title={
          <Title level={3} className="mb-0">
            {props.record ? 'Edit Address' : 'Add Address'}
          </Title>
        }
        onOk={() => setVisible(false)}
        onCancel={() => { setVisible(false); form.resetFields();}}
        footer={[
          <Button key="back" onClick={() => setVisible(false)}>
            Close
          </Button>,
          <Button key="submit" type="primary" htmlType="submit" loading={loading} onClick={handleSubmit}>
            Submit
          </Button>,
        ]}
      >
        {/* Hidden native inputs to catch browser autofill */}
        {visible && (
          <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}>
            <input ref={streetRef}  name="address-line1-native" autoComplete="address-line1" />
            <input ref={cityRef}    name="address-level2-native" autoComplete="address-level2" />
            <input ref={stateRef}   name="address-level1-native" autoComplete="address-level1" />
            <input ref={zipRef}     name="postal-code-native"    autoComplete="postal-code" />
            <input ref={countryRef} name="country-native"        autoComplete="country-name" />
          </div>
        )}

        <Form form={form} name="Add / Edit Address" onFinish={handleSubmit} autoComplete="on">
          <Form.Item
            name="name"
            label="Name"
            {...formItemLayout}
            rules={[{ required: true, message: 'Please input a name' }]}
            initialValue={props.record?.name}
          >
            <Input placeholder="Name" className="w-full" />
          </Form.Item>

          <Form.Item
            label="Type"
            {...formItemLayout}
            name="type"
            rules={[{ required: true, message: 'Please input a type' }]}
            initialValue={props.record?.type}
          >
            <Select 
                placeholder="Select a type"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
            >
              <Select.Option value="Shipping">Shipping</Select.Option>
              <Select.Option value="Billing">Billing</Select.Option>
              <Select.Option value="Billing and Shipping">Shipping and Billing</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Street"
            {...formItemLayout}
            name="street"
            rules={[{ required: true, message: 'Please input a street' }]}
            initialValue={props.record?.street}
          >
            <Input 
                placeholder="Address line 1" 
                className="w-full" 
                autoComplete="address-line1"
             />
          </Form.Item>

          <Form.Item
            label="City"
            {...formItemLayout}
            name="city"
            rules={[{ required: true, message: 'Please input a city' }]}
            initialValue={props.record?.city}
          >
            <Input placeholder="City" className="w-full" autoComplete="address-level2" />
          </Form.Item>

          <Form.Item
            label="Country"
            {...formItemLayout}
            name="country"
            rules={[{ required: true, message: 'Please input a country' }]}
            initialValue={{
              key: props.record?.country.code,
              label: props.record?.country.name,
            }}
          >
            <SelectCountries />
          </Form.Item>

          <div className="flex flex-row">
            <Form.Item dependencies={['country']} noStyle>
              {({ getFieldValue }) => {
                return getFieldValue('country') ? (
                  <Form.Item
                    label="State"
                    {...formItemLayout}
                    name="state"
                    rules={[{ required: true, message: 'Please input a state' }]}
                    initialValue={{
                      key: props.record?.state,
                      label: props.record?.state,
                    }}
                    dependencies={['country']}
                  >
                    <SelectStates
                      mode="default"
                      countryShortCode={form.getFieldValue('country')?.key}
                      disabled={form.getFieldValue('country')?.key === undefined ? true : false}
                    />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item
              label="Zip Code"
              {...formItemLayout}
              name="zip_code"
              rules={[{ required: true, message: 'Please input a zip code' }]}
              initialValue={props.record?.zip_code}
              style={{ marginLeft: '10px' }}
            >
              <Input placeholder="ZIP / Postal code" autoComplete="postal-code" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default AddEdit;
